/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import * as React from 'react';
import { IEveMapViewProps } from './eve-map';
import {
    GetFullStoreLocationsWithAvailabilitiesInput,
    IGetFullStoreLocationsWithAvailabilitiesData
} from '../../actions/get-full-store-locations-with-availabilities.action';
import { IEveMapData } from './eve-map.data';
import { IPushpinOptionsData } from './eve-map.props.autogenerated';

declare global {
    var _noInfoBox: boolean | undefined;
    var _closestLocationId: undefined | string;
    var _closestLocationDistance: string | undefined;
    var _searchZip: string | undefined;
    var _closestMarkerPosition: [number, number] | undefined;
}

const EveMapView = (props: IEveMapViewProps) => {
    const { mapContainer, isInit } = useBingMap(props);
    useInfoBox(props);

    return (
        <>
            {isInit && (
                <div
                    style={{ position: 'absolute', top: 0, left: 0, zIndex: 2, height: '100%', width: '100%' }}
                    className='cps-location__map-skeleton'
                ></div>
            )}
            <div
                ref={mapContainer}
                style={{ position: 'absolute', top: 0, left: 0, zIndex: 0, height: '100%', width: '100%', display: 'none' }}
            ></div>
        </>
    );
};

function useBingMap(props: IEveMapViewProps) {
    const { context, data, config } = props;
    const [{ AzureMapsApiKey: BackupAzureMapsApiKey }] = React.useState(
        data.fullStoreLocationsWithAvailabilities?.result ?? { AzureMapsApiKey: '' }
    );
    const { AzureMapsApiKey } = context.request.channel ?? {};

    const [atlas, setAtlas] = React.useState<typeof globalThis.atlas>();
    const [map, setMap] = React.useState<typeof globalThis.map>();
    const [isInit, setIsInit] = React.useState(true);
    const mapContainer = React.useRef<HTMLDivElement>(null);

    // Load azure package once on load
    React.useEffect(() => {
        if (!isInit) {
            return;
        }

        window._noInfoBox = undefined;
        window._closestLocationId = undefined;
        window._closestLocationDistance = undefined;
        window._searchZip = undefined;
        window._closestMarkerPosition = undefined;

        void import('azure-maps-control').then(r => {
            loadCss();
            setAtlas(r);
            context.actionContext.update<IGetFullStoreLocationsWithAvailabilitiesData>(new GetFullStoreLocationsWithAvailabilitiesInput(), {
                Locations: getLocations(data.fullStoreLocationsWithAvailabilities)
            });
            globalThis.atlas = r;
        });
    }, [isInit]);

    // Create Azure map and markers (pushpins) only once on load
    React.useEffect(() => {
        if (!atlas || !mapContainer.current || map) {
            return;
        }

        const _map = new atlas.Map(mapContainer.current, {
            authType: atlas.AuthenticationType.subscriptionKey,
            subscriptionKey: AzureMapsApiKey?.length ? AzureMapsApiKey : BackupAzureMapsApiKey
        });
        const markers = createMarkers(props, _map, atlas);

        for (const m of markers ?? []) {
            m._addEventListener(
                'click',
                () => {
                    window._noInfoBox = false;
                },
                true
            );
        }
        _map.controls.add(new atlas.control.ZoomControl(), {
            position: atlas.ControlPosition.BottomRight
        });
        _map.setCamera({ zoom: 3.5, center: [-98.58, 39.83] });
        if (markers) {
            _map.markers.add(markers);
        }
        mapContainer.current.style.removeProperty('display');
        globalThis.map = _map;

        setMap(_map);

        _map.events.add('ready', () => {
            setIsInit(false);
        });
    }, [atlas]);

    React.useEffect(() => {
        if (!globalThis.window?.map) {
            return;
        }
        const markerPaths = window.map.markers.getMarkers().map(m => m.getElement().querySelector('path'));
        for (const p of markerPaths) {
            p?.style.removeProperty('fill');
            p?.parentElement?.parentElement?.style.removeProperty('z-index');
        }
        const targetPath = document.querySelector<SVGPathElement>(
            `.az-marker._${data.fullStoreLocationsWithAvailabilities?.result?.SelectedId} path`
        );
        if (config.pushpinOptions?.selectionColor && targetPath && targetPath.parentElement?.parentElement) {
            targetPath.style.fill = config.pushpinOptions.selectionColor;
            targetPath.parentElement.parentElement.style.zIndex = '5';
        }
    }, [data.fullStoreLocationsWithAvailabilities?.result?.SelectedId]);

    return { mapContainer, isInit };
}

function useInfoBox(props: IEveMapViewProps) {
    const { data } = props;

    React.useEffect(() => {
        const result = data.fullStoreLocationsWithAvailabilities?.result;
        const index = result?.Locations.findIndex(({ Id }) => Id === result.SelectedId) ?? -1;
        const location = result?.Locations.find(({ Id }) => Id === result.SelectedId)!;

        if (index === -1 || !window.map || !window.atlas) {
            return;
        }

        window.infoBox?.remove();
        window.infoBox = [location.Latitude, location.Longitude].every(value => value !== undefined)
            ? new window.atlas.Popup({
                  content: `
                         <div class="cps-map__info-box">
                             <div>${location.StoreName} - ${location.StorePurpose}</div>
                             <div>
                                 <div>${location.StreetAddress}</div>
                                 <div>${`${location.City}, ${location.State} ${location.Zip}`}</div>
                             </div>
                             <button onclick="(() => (document.querySelector('.cps-location--focused').scrollIntoView({ behavior: 'smooth',block: 'center',inline: 'center' })))()">View Details</button>
                             ${
                                 _closestLocationId === location.Id && _searchZip
                                     ? `<div style='font-weight:650;'>Closest to zip code ${_searchZip} - ${_closestLocationDistance} miles</div>`
                                     : ''
                             } 
                         </div>`,
                  draggable: false,
                  position: [location.Longitude, location.Latitude],
                  showPointer: false
                  //   pixelOffset: [0, -60]
              })
            : null;

        window.infoBox?.open(window.map);
    });
}

function createMarkers(props: IEveMapViewProps, map: typeof globalThis.map, atlas: typeof globalThis.atlas) {
    if (!atlas) {
        return;
    }

    const { data, config } = props;
    const locations = getLocations(data.fullStoreLocationsWithAvailabilities).filter(({ Longitude, Latitude }) => Longitude && Latitude);

    window.removeFocusedLocation = () => setSelectedLocationId(props);

    return locations.map((location, i) => {
        var marker = new atlas.HtmlMarker({
            ...getPushpinOptions(
                location.Id,
                location.Id === data.fullStoreLocationsWithAvailabilities?.result?.SelectedId,
                i,
                config.pushpinOptions
            ),
            position: [location.Longitude, location.Latitude]
        });

        map.events.add('click', marker, () => {
            setSelectedLocationId(props, location);
            window.infoBox?.remove();
            window.infoBox = new atlas.Popup({
                content: `
                         <div class="cps-map__info-box">
                             <div>${location.StoreName} - ${location.StorePurpose}</div>
                             <div>
                                 <div>${location.StreetAddress}</div>
                                 <div>${`${location.City}, ${location.State} ${location.Zip}`}</div>
                             </div>
                             <button onclick="(() => (document.querySelector('.cps-location--focused').scrollIntoView({ behavior: 'smooth',block: 'center',inline: 'center' })))()">View Details</button>
                         </div>`,
                position: [location.Longitude, location.Latitude],
                showPointer: false,
                pixelOffset: [0, -60]
            });
            window.infoBox.open(map);
        });

        return marker;
    });
}

function getPushpinOptions(id: string, isSelectedLocation: boolean, index: number, pushpinOptions?: IPushpinOptionsData) {
    const text = pushpinOptions?.showIndex ? (index + 1).toString() : undefined;
    const size = pushpinOptions?.size || 1;
    const color = isSelectedLocation ? pushpinOptions?.selectionColor || pushpinOptions?.color : pushpinOptions?.color;
    const htmlContent = getSvgIcon(id, size, color, text, pushpinOptions?.selectionColor);

    return {
        color: isSelectedLocation ? pushpinOptions?.selectionColor : pushpinOptions?.color,
        htmlContent
    };
}

function getSvgIcon(id: string, size: number, color?: string, text?: string, selectionColor?: string) {
    const baseValue: number = 32;
    return `<svg xmlns="http://www.w3.org/2000/svg" class="az-marker _${id}" width="${baseValue * size}" height="${baseValue *
        size}" viewBox="0 0 365 560">
                <style>
                    .az-marker {
                        cursor: pointer;
                    }
                    .az-marker path {
                      transition: fill 0.2s ease-out; 
                    }
                    .az-marker path:hover {
                      fill: ${selectionColor ?? 'black'};
                    }
                </style>
                <path stroke="white" stroke-width="20" fill="${color}" d="M182.9,551.7c0,0.1,0.2,0.3,0.2,0.3S358.3,283,358.3,194.6c0-130.1-88.8-186.7-175.4-186.9 C96.3,7.9,7.5,64.5,7.5,194.6c0,88.4,175.3,357.4,175.3,357.4S182.9,551.7,182.9,551.7z" />
                ${text ? `<text x="185" y="280" style="font-size:220px;fill:#ffffff;" text-anchor="middle">${text}</text>` : ''}
            </svg>`;
}

function getLocations(input: IEveMapData['fullStoreLocationsWithAvailabilities']) {
    return input?.result?.Locations ?? [];
}

function setSelectedLocationId(props: IEveMapViewProps, location?: IGetFullStoreLocationsWithAvailabilitiesData['Locations'][0]) {
    const { context, data } = props;
    context.actionContext.update<IGetFullStoreLocationsWithAvailabilitiesData>(new GetFullStoreLocationsWithAvailabilitiesInput(), {
        Locations: getLocations(data.fullStoreLocationsWithAvailabilities),
        SelectedId: location?.Id
    });
}

function loadCss() {
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = 'https://atlas.microsoft.com/sdk/javascript/mapcontrol/3/atlas.min.css';
    link.type = 'text/css';
    document.head.appendChild(link);
}

export default EveMapView;
